import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import CitedMarkdown from '../../../../../components/markdown/CitedMarkdown';
import { label as tLabel } from '../../../report/utils/reportLabelTranslations';
import { joinNames } from '../../../../../utils/format';

/** Blocks shown for each argument, in order; a block is skipped when its fields are empty. */
const ARGUMENT_BLOCKS = [
  { key: 'explanation', tone: 'border-blue-300 bg-blue-50/50', title: (lang) => tLabel('Explanation', lang), fields: ['Explanation'] },
  { key: 'evidence', tone: 'border-green-300 bg-green-50/50', title: (lang) => tLabel('Evidence', lang), fields: ['Evidence'], cited: 500 },
  { key: 'external', tone: 'border-teal-300 bg-teal-50/50', title: () => 'External Evidence (Knowledge Base)', fields: ['External_evidence'], sources: 300 },
  { key: 'major', tone: 'border-amber-300 bg-amber-50/50', title: () => 'Major Discussion Point', fields: ['Major discussion point'] },
  { key: 'agreements', tone: 'border-emerald-300 bg-emerald-50/50', title: () => 'Agreements', fields: ['Agreed with', 'Agreed on'], labelled: true },
  { key: 'disagreements', tone: 'border-red-300 bg-red-50/50', title: () => 'Disagreements', fields: ['Disagreed with', 'Disagreed on'], labelled: true },
];

function ArgumentBlock({ block, argument, lang, citations }) {
  const present = block.fields.filter((field) => argument[field]);
  if (present.length === 0) return null;
  const value = argument[present[0]];

  let body;
  if (block.labelled) {
    body = present.map((field) => (
      <div key={field} className="text-sm text-gray-700 mt-1">
        <span className="font-semibold">{field}:</span> {joinNames(argument[field])}
      </div>
    ));
  } else if (block.cited) {
    body = <CitedMarkdown text={value} maxLength={block.cited} className="text-sm text-gray-700 leading-relaxed" {...citations} />;
  } else if (block.sources) {
    body = (
      <CitedMarkdown
        text={value}
        maxLength={block.sources}
        className="text-sm text-gray-700 leading-relaxed"
        sourceReferences={citations.sourceReferences}
        onCitationClick={citations.onCitationClick}
      />
    );
  } else {
    body = <div className="text-sm text-gray-700 leading-relaxed">{value}</div>;
  }

  return (
    <div className={`border-l-2 rounded-r-lg p-4 ${block.tone}`}>
      <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">{block.title(lang)}</div>
      {body}
    </div>
  );
}

function SpeakerCard({ name, speakerArguments, lang, citations }) {
  const [open, setOpen] = useState(false);
  const argumentNames = Object.keys(speakerArguments);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-gray-200 hover:from-purple-100 hover:to-indigo-100 text-left flex items-center gap-4"
      >
        <span className="h-11 w-11 rounded-full bg-purple-100 border-2 border-purple-200 flex items-center justify-center text-purple-700 font-bold text-lg flex-shrink-0">
          {name.charAt(0).toUpperCase()}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-lg font-bold text-gray-900">{name}</span>
          <span className="text-sm text-gray-600">
            {argumentNames.length} {argumentNames.length === 1 ? 'argument' : 'arguments'}
          </span>
        </span>
        <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="p-6 space-y-6 bg-gray-50/50">
          {argumentNames.map((argumentName, idx) => (
            <div key={argumentName} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Argument {idx + 1}</div>
                <h4 className="text-lg font-bold text-gray-900 leading-tight">{argumentName}</h4>
              </div>
              <div className="p-5 space-y-4">
                {ARGUMENT_BLOCKS.map((block) => (
                  <ArgumentBlock
                    key={block.key}
                    block={block}
                    argument={speakerArguments[argumentName]}
                    lang={lang}
                    citations={citations}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const parseJson = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/** Speakers and their arguments. The session field (JSON) wins: it carries editor changes and translations. */
export default function SpeakersTab({ report, onCitationClick }) {
  const { markdown, data, lang, isTranslated, enrichment } = report;
  const citations = {
    transcriptReferences: enrichment.transcriptReferences,
    sourceReferences: enrichment.sourceReferences,
    onCitationClick,
  };

  const parsed = markdown.speakers ? parseJson(markdown.speakers) : null;
  if (markdown.speakers && !parsed && (isTranslated || !data?.Speakers)) {
    // Legacy markdown or plain translated text.
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <CitedMarkdown text={markdown.speakers} maxLength={1500} sourceReferences={enrichment.sourceReferences} />
      </div>
    );
  }

  const speakers = parsed || data?.Speakers;
  if (!speakers) {
    return (
      <div className="session-detail-empty">
        <strong>No speaker analysis yet.</strong>
        <span>Generate the session report to see each speaker's arguments.</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(speakers).map(([name, speakerArguments]) => (
        <SpeakerCard key={name} name={name} speakerArguments={speakerArguments} lang={lang} citations={citations} />
      ))}
    </div>
  );
}
